import React, { Fragment } from "react";
import { graphql } from "gatsby";
import { Layout } from "~/templates/Layout";
import { DownloadCard } from "~/molecules/DownloadCard";
import { PATH } from "~/constant/path";
import { PageHeading } from "~/atoms/PageHeading";
import { Container } from "~/atoms/Container";
import { Pager } from "~/molecules/Pager";
import { colors } from "~/utils/colors";
import { breakPoints, fontFamily } from "~/utils/variables";

interface Props {
  data: {
    allContentfulDownload: GatsbyTypes.ContentfulDownloadConnection;
  };
  pageContext: {
    limit: number;
    skip: number;
    totalPages: number;
    currentPage: number;
  };
}

const DESCRIPTION = ["リファラル採⽤や⼈材採⽤に関する", "お役⽴ち資料を無料でダウンロードいただけます"];

const IndexDownload: React.FC<Props> = ({ data, pageContext }) => {
  const { allContentfulDownload } = data;
  const { currentPage, totalPages } = pageContext;

  // NOTE: pathSlug === "request"を常に最初、pathSlug === "casestudy"をその次に表示
  const sortedEdges = [...allContentfulDownload.edges].sort((a, b) => {
    if (a.node.pathSlug === "request") return -1;
    if (b.node.pathSlug === "request") return 1;
    if (a.node.pathSlug === "casestudy") return -1;
    if (b.node.pathSlug === "casestudy") return 1;
    return 0;
  });

  return (
    <>
      <style jsx>{`
        .pageWrapper {
          -webkit-font-smoothing: antialiased;
          background: ${colors.bgGrayGradient};
          font-family: ${fontFamily.insight};
          font-feature-settings: "palt";
          padding-bottom: 50px;
          text-rendering: optimizeLegibility;
        }
        .pageWrapper :global(h1) {
          font-weight: 700;
        }
        .downloadList {
          display: flex;
          flex-wrap: wrap;
          position: relative;
          width: 100%;
        }
        @media (${breakPoints.sp}) {
          .pageWrapper {
            padding-bottom: 40px;
          }
          .downloadList {
            box-sizing: border-box;
            padding: 0 16px;
          }
        }
      `}</style>
      <Layout
        title={PATH.DOWNLOADS.name}
        ogpDescription={DESCRIPTION.join("")}
        breadCrumbItems={[PATH.TOP, PATH.DOWNLOADS]}
      >
        <div className="pageWrapper">
          {currentPage === 1 && (
            <PageHeading title={PATH.DOWNLOADS.name}>
              {DESCRIPTION.map((str, index) => (
                <Fragment key={`${str}_${index}`}>
                  {DESCRIPTION.length - 1 === index ? (
                    str
                  ) : (
                    <>
                      {str}
                      <br />
                    </>
                  )}
                </Fragment>
              ))}
            </PageHeading>
          )}
          <Container>
            <ul className="downloadList">
              {sortedEdges.map((edge) => {
                const { node } = edge;
                // NOTE: pathSlug === "request"の場合は/requestにリンク
                const href = node.pathSlug === "request" ? `/request` : `/downloads/${node.pathSlug}`;

                return <DownloadCard key={node.contentful_id} article={node} href={href} />;
              })}
            </ul>
          </Container>
          <Pager currentPage={currentPage} totalPages={totalPages} basePath={PATH.DOWNLOADS.to} />
        </div>
      </Layout>
    </>
  );
};

export default IndexDownload;

export const indexDownloadQuery = graphql`
  query IndexDownload($limit: Int!, $skip: Int!) {
    allContentfulDownload(sort: { fields: createdAt, order: DESC }, limit: $limit, skip: $skip) {
      edges {
        node {
          contentful_id
          title
          pathSlug
          shortDescription
          documentCover {
            fluid(maxWidth: 767) {
              src
              aspectRatio
            }
          }
          tag {
            name
            slug
          }
        }
      }
      totalCount
    }
  }
`;
